
import { memo,  useEffect, useRef } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router";
import { useDispatch } from "react-redux";
import { v4 } from "uuid";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useAutoSave } from "./hooks/useAutoSave";
import FormSection from "./components/features/FormSection";
import { deleteSection, sectionAutoSave, setFormData } from "./slices/FbSections";
import { fieldTypeDefaults } from "./utils/schemas/initial";
import { notify } from "../../bmr/utils/methods/notification";

const FormContent = ({
  SectionSchema,
  selectedSection,
  selectedField,
  setSelectedSection,
  setSelectedField,
  setIsSectionAdd,
  isSectionAdd,
  setIsFieldAdd,
  isFieldAdd
}) => {
  const { control, setValue, getValues } = useFormContext();
  const {
    fields: sections,
    append,
    move: sectionMove,
    remove,
  } = useFieldArray({ control, name: "sections" });
  const sectionSchema = useWatch({ control, name: "sections" });
  const dispatch = useDispatch();
  const { id } = useParams();

  const addNewSection = async () => {
    try{setIsSectionAdd(true)
    append({
      ...SectionSchema,
      section_uuid: v4(),
      order: sections.length + 1,
      fields: [
        {
          ...SectionSchema?.fields[0],
          input_uuid: v4(),
          order: 1,
        },
      ],
    });
    const updatedSections = getValues("sections");
    const clonedSections = JSON.parse(JSON.stringify(updatedSections));
    dispatch(setFormData({ sections: clonedSections }));
    const response = await dispatch(sectionAutoSave({
      id: id,
      data: { sections: clonedSections }
    })).unwrap();
    if (response) {
      notify.success("Section added successfully")
    }}catch{
      setIsSectionAdd(false)
    }finally{
      setIsSectionAdd(false)
    }

  };

  //@info adding this to remove section.
  const removeSection = async ({ sectionId, index }) => {
    if (sections?.length === 1) {
      console.warn("Cannot remove the last section.");
      return;
    }
    try {
      remove(index);
      const updatedSections = getValues("sections");
      const clonedSections = JSON.parse(JSON.stringify(updatedSections));
      dispatch(setFormData({ sections: clonedSections }));

      if (id && sectionId) {
       const resp=await dispatch(deleteSection({ id, sectionId }));
       if(resp.payload.success){
        notify.success(resp.payload.message)
       }
      } else {
        console.warn("Missing `id` or `sectionId` for deleteSection dispatch.");
      }
    } catch (error) {
      console.error("Error removing section:", error);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleSectionDragEnd = (event) => {
  const { active, over } = event;
  if (!over || active.id === over.id) return;

  const oldIndex = sections.findIndex((s) => s.section_uuid === active.id);
  const newIndex = sections.findIndex((s) => s.section_uuid === over.id);

  sectionMove(oldIndex, newIndex);

  const updatedSections = getValues("sections").map((section, idx) => ({
    ...section,
    order: idx + 1,
  }));
  setValue("sections", updatedSections);
};

 useAutoSave({
    saveFunction: async (data) => {
      return await dispatch(sectionAutoSave({ data, id }));
    },
  });

 const handleSectionDragStart = async () => {
    const currentFormData = getValues();
    await dispatch(sectionAutoSave({ data: currentFormData, id }));
  };
  const prevFieldTypeRef = useRef();

  useEffect(() => {
    const sectionIndex = sectionSchema.findIndex(
      (section) => section?.section_uuid === selectedSection
    );

    const fieldIndex =
      sectionIndex !== -1
        ? sectionSchema[sectionIndex]?.fields?.findIndex(
            (field) => field?.input_uuid === selectedField
          )
        : -1;

    const field_type =
      sectionSchema?.[sectionIndex]?.fields?.[fieldIndex]?.property?.field_type;

    const field_layout =
      sectionSchema?.[sectionIndex]?.fields?.[fieldIndex]?.property
        ?.field_layout;

    if (
      field_type &&
      fieldTypeDefaults[field_type] &&
      prevFieldTypeRef.current !== field_type
    ) {
      setValue(`sections.${sectionIndex}.fields.${fieldIndex}.property`, {
        field_layout,
        field_type,
        ...fieldTypeDefaults[field_type],
      });
      prevFieldTypeRef.current = field_type;
    }
  }, [selectedSection, selectedField, sectionSchema]);

  return (
    <>
      {/* <h1>{saveStatus}</h1> */} {/* @info to show auto save status */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleSectionDragEnd}
        onDragStart={handleSectionDragStart}
      >
        <SortableContext
          items={sections.map((s) => s.section_uuid)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map((section, index) => (
            <FormSection
              key={section?.section_uuid}
              index={index}
              section={section}
              selectedSection={selectedSection}
              setSelectedSection={setSelectedSection}
              selectedField={selectedField}
              setSelectedField={setSelectedField}
              addNewSection={addNewSection}
              removeSection={removeSection}
              noOfSections={sections?.length}
              isSectionAdd={isSectionAdd}
              isFieldAdd={isFieldAdd}
              setIsFieldAdd={setIsFieldAdd}
            />
          ))}
        </SortableContext>
      </DndContext>
    </>
  );
};

export default memo(FormContent);